import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import z from 'zod';
import { useOrganizationContext } from '../organization.context';
import {
  Form,
  FormField,
  FormLabel,
  FormItem,
  FormControl,
  FormDescription,
} from '@/components/ui/form';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { updateOrganization } from '@/api/organization.api.private';
import { useToast } from '@/components/ui/use-toast';
import { useState } from 'react';

const EMAIL_TEMPLATES = ['invite-user'];

const EMAILS_SCHEMA = z.object({
  emails: z.record(
    z.string().refine(value => EMAIL_TEMPLATES.includes(value), {
      message: 'Invalid email template',
    }),
    z.object({
      body: z.string(),
    }),
  ),
});

export default function NotificationSettings() {
  const { setOrganization, organization } = useOrganizationContext();
  const { toast } = useToast();

  const orgName = organization.name;
  const emails = organization.internalConfigurations?.emails ?? { 'invite-user': { body: '' } };
  const [loading, setLoading] = useState(false);

  const onSubmit = async formData => {
    setLoading(true);
    const { data, error } = await updateOrganization({
      data: { internalConfigurations: { emails: formData.emails } },
      id: organization.id,
    });

    if (error) {
      setLoading(false);
      return toast({
        title: 'Error update organization email settings',
        description: 'An error occurred while updating update organization email settings',
        status: 'error',
      });
    }

    setOrganization(oldOrganization => ({ ...oldOrganization, ...data }));
    toast({
      title: 'Notification settings as been updated',
      status: 'success',
    });
    setLoading(false);
  };

  const form = useForm({
    resolver: zodResolver(EMAILS_SCHEMA),
    defaultValues: { emails },
  });

  const resetToDefault = async () => {
    const restData = { emails: { 'invite-user': { body: '' } } };
    await onSubmit(restData);
    form.reset(restData);
  };

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>Notification Settings</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex-1 flex flex-col">
            <FormField
              name="emails.invite-user.body"
              control={form.control}
              render={({ field }) => (
                <FormItem className="flex-1 flex flex-col">
                  <FormLabel className="font-bold">Invite User Email</FormLabel>
                  <FormControl>
                    <Textarea className="flex-1 min-h-[15rem] resize-none" {...field} />
                  </FormControl>
                  <FormDescription>
                    Add text here to replace the body of the{' '}
                    <HoverCard>
                      <HoverCardTrigger className="underline">
                        default invite email
                      </HoverCardTrigger>
                      <HoverCardContent>
                        You have been invited to join C8 Health, the exclusive and all inclusive
                        knowledge platform for {orgName}.<br />
                        <br />
                        Whether you are in between rounds or grabbing a quick coffee, we are here to
                        make sure you have the right info when you need it. Access the platform
                        through our web app or download our mobile app (iOS or Android). Your future
                        self will thank you
                      </HoverCardContent>
                    </HoverCard>
                  </FormDescription>
                </FormItem>
              )}
            />

            <div className="flex justify-between items-center mt-4">
              <Button type="submit" loading={loading}>
                Save
              </Button>
              <Button disabled={loading} onClick={resetToDefault}>
                Use default
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
